/**
 * Re-prices a stored carrier quote at a different premium.
 *
 * Carriers are scraped at fixed premiums ($10,000 a year for 10-pay, about $100,000 single pay),
 * so any other premium is an ESTIMATE made from the nearest stored quote: linear for most
 * products, corrected for Lincoln MoneyGuard and OneAmerica Asset Care (band tables, plus a
 * measured rule for Lincoln 10-Pay), always clamped to the quote's own
 * `investmentMin`..`investmentMax`. Moved here from the client on 16 Sep 2026 so Shop
 * Insurance and the email agent's quote search price the same premium the same way.
 */

import { FundingPolicyType } from '../appModel';
import { isCareMattersAnnuity } from '../FundingPolicyTypeDef';
import {
  exceedsLincolnFaceAmountLimit,
  getLincolnMaximumPremium,
  getLincolnScalingFactor,
} from './lincolnPricingUtils';
import {
  getOAMultipliersForPremium,
  hasCalibratedOABands,
  isOneAmericaAssetCare,
} from './oneAmericaPricingUtils';
import {
  getThreeYearAobPremiumRange,
  getThreeYearAobScalingFactor,
  isThreeYearAobAssetCareQuote,
} from './oneAmericaThreeYearAobPricing';

/**
 * Why a quote is not shown at the requested premium. Each value also records the direction
 * the premium moved, so a caller can label it truthfully without knowing what was requested.
 *
 * - 'max' / 'min'                 clamped to the quote's investmentMax / investmentMin
 * - 'max-unknown' / 'min-unknown' that bound is missing, so pinned to the quote's own premium
 * - 'not-priceable'               no validated carrier curve can price the request, so pinned
 *                                 to the quote's own premium
 */
export type PremiumAdjustmentLimit =
  | 'min'
  | 'max'
  | 'min-unknown'
  | 'max-unknown'
  | 'not-priceable';

/** The fields re-pricing reads and resets, on top of the stored quote. */
export type PricedQuote = PolicyQuote & {
  premiumClamped?: PremiumAdjustmentLimit;
  projectedCostCoverage?: number;
  roi?: number;
  roiValues?: unknown;
  formattedROI?: string;
};

/**
 * investmentMin/investmentMax are NUMERIC NOT NULL and the bulk importer writes 0 when the
 * source CSV has no value, so a non-positive bound means "unknown", never "unbounded".
 * Returns undefined for unknown bounds so callers can refuse to extrapolate.
 */
export function toKnownInvestmentBound(value: number | null | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

const SECURIAN_MIN_DEATH_BENEFIT_CAP = 10000;
const SECURIAN_MIN_DEATH_BENEFIT_RATE = 0.1;

function calculateMinGuaranteedDeathBenefit(
  fundingPolicyType: number,
  scaledDeathBenefit: number,
  originalValue: number | undefined,
  scalingFactor: number,
  usaState?: string,
): number | undefined {
  // CareMatters II in New York: the residual death benefit is the LESSER of
  // 10% of the specified amount and $25,000 — a capped quantity, so scaling it
  // linearly with the premium pushes it past the cap on any quote whose
  // specified amount exceeds $250,000. Every other state pays a flat 20% of
  // the specified amount, which does scale linearly. Confirmed against all
  // 5,472 NY rows and the loaded non-NY rows (ratio exactly 0.2000).
  if (fundingPolicyType === FundingPolicyType.careMattersTwo && usaState?.toUpperCase() === 'NY') {
    return Math.min(25000, scaledDeathBenefit * 0.1);
  }
  if (
    fundingPolicyType === FundingPolicyType.secureCareThree ||
    fundingPolicyType === FundingPolicyType.secureCareFour
  ) {
    return Math.min(
      SECURIAN_MIN_DEATH_BENEFIT_CAP,
      scaledDeathBenefit * SECURIAN_MIN_DEATH_BENEFIT_RATE,
    );
  }
  if (fundingPolicyType === FundingPolicyType.nylAssetFlex) {
    // according to the NYL asset flex product details
    // the minimum guaranteed death benefit is the lesser of 25k or 10% of the death benefit
    return Math.min(25000, scaledDeathBenefit * 0.1);
  }
  return originalValue ? originalValue * scalingFactor : undefined;
}

/**
 * `outOfRange` means the carrier cannot price the requested premium, so the factors carry no
 * information and callers must not scale benefits by them. `maximumPremium`, when present, is
 * the highest premium the carrier can price for this quote, so the quote can be capped there
 * instead of pinned to its own premium.
 */
export type QuoteScalingFactors = {
  db: number;
  ltc: number;
  outOfRange?: boolean;
  maximumPremium?: number;
};

const UNPRICEABLE_FACTORS: QuoteScalingFactors = { db: 1, ltc: 1, outOfRange: true };

export function getScalingFactorsForQuote(
  quote: PricedQuote,
  newPremium: number,
): QuoteScalingFactors {
  const linearFactor = newPremium / quote.premiumAmount;

  if (quote.fundingPolicyType === FundingPolicyType.moneyGuardFixedAdvantage) {
    // No factor means no validated curve prices this premium: it is past the single pay band
    // table or the measured 10-Pay range, or the pay structure was never calibrated.
    const factor = getLincolnScalingFactor(quote, newPremium);
    if (factor === undefined) return UNPRICEABLE_FACTORS;
    // Lincoln refuses to illustrate a premium whose face amount passes its issue limit, so
    // report the highest premium it will write and let the caller cap the quote there.
    if (
      newPremium > quote.premiumAmount &&
      exceedsLincolnFaceAmountLimit(quote.deathBenefitInitial * factor)
    ) {
      return { ...UNPRICEABLE_FACTORS, maximumPremium: getLincolnMaximumPremium(quote) };
    }
    return { db: factor, ltc: factor };
  }

  // A 3-year AOB quote is priced from itself, inside the range its own death benefit allows.
  // Asked for more than that, it is capped at the top of the range like any other quote.
  if (isThreeYearAobAssetCareQuote(quote)) {
    const factor = getThreeYearAobScalingFactor(quote, newPremium);
    if (factor !== null) return { db: factor, ltc: factor };
    const range = getThreeYearAobPremiumRange(quote);
    return {
      ...UNPRICEABLE_FACTORS,
      maximumPremium: range && newPremium > range.max ? range.max : undefined,
    };
  }

  if (isOneAmericaAssetCare(quote.fundingPolicyType)) {
    // No calibrated band table for this pay structure (e.g. 20-Pay) or AOB period
    // (e.g. 3 years). Repricing would silently borrow another curve, so hold the
    // quote at its scraped premium instead of producing benefits the carrier never
    // quoted.
    if (
      !hasCalibratedOABands(
        quote.cobPaymentOption,
        quote.productFundingOption,
        quote.aobDuration,
        quote.usaStateCategory,
      )
    ) {
      return UNPRICEABLE_FACTORS;
    }
    const original = getOAMultipliersForPremium(
      quote.fundingPolicyType,
      quote.productFundingOption,
      quote.aobInflation,
      quote.cobInflation,
      quote.cobDuration,
      quote.premiumAmount,
      quote.usaStateCategory,
      quote.primaryAge,
      quote.primaryGender,
      quote.aobDuration,
      quote.secondaryAge,
      quote.primaryTobaccoStatus,
      quote.inflationDuration,
    );
    const target = getOAMultipliersForPremium(
      quote.fundingPolicyType,
      quote.productFundingOption,
      quote.aobInflation,
      quote.cobInflation,
      quote.cobDuration,
      newPremium,
      quote.usaStateCategory,
      quote.primaryAge,
      quote.primaryGender,
      quote.aobDuration,
      quote.secondaryAge,
      quote.primaryTobaccoStatus,
      quote.inflationDuration,
    );
    // A missing demographic Single Pay curve is unsafe to approximate linearly. Keep legacy
    // no-band behavior for every other OA path, and retain the known maximum for above-band.
    if (original.miss === 'unpriceable' || target.miss === 'unpriceable') {
      return UNPRICEABLE_FACTORS;
    }
    if (original.miss === 'above-band' || target.miss === 'above-band') {
      return {
        ...UNPRICEABLE_FACTORS,
        maximumPremium: original.miss === 'above-band' ? undefined : target.maximumPremium,
      };
    }
    if (!original.db || !original.ltc) return UNPRICEABLE_FACTORS;
    return {
      db: linearFactor * (target.db / original.db),
      ltc: linearFactor * (target.ltc / original.ltc),
    };
  }

  return { db: linearFactor, ltc: linearFactor };
}

function scaleValue(value: number | undefined, scalingFactor: number): number | undefined {
  return value !== undefined ? value * scalingFactor : undefined;
}

/**
 * CareMatters Annuity rider premiums are a fixed share of the single premium, and the quote
 * row stores the dollar amount at the scraped premium, so they have to move with it. Carrier
 * illustrations for one stored quote (inflation rider $25,000 at $100,000) print $23,250 at
 * $93,000 and $44,750 at $179,000: 25% each time. Nonforfeiture behaves the same: a quote
 * storing $1,500 prints $1,852 at $123,450 and $1,851 at $123,420.
 *
 * Rounded to the nearest dollar, which is what those two nonforfeiture figures show the
 * carrier doing (1,851.75 up, 1,851.30 down). A rider that was not elected is left as stored.
 */
function scaleCareMattersAnnuityRiderPremiums(
  quote: PricedQuote,
  premium: number,
): Pick<PricedQuote, 'params' | 'extensionOfBenefitsNonforfeitureRiderAmount'> {
  const atPremium = (stored: unknown): number | null => {
    if (stored === null || stored === undefined || stored === '') return null;
    const amount = Number(stored);
    return Number.isFinite(amount) ? Math.round((amount * premium) / quote.premiumAmount) : null;
  };

  const inflation = atPremium(quote.params?.inflationProtectionPremium);
  const nonforfeiture = atPremium(quote.extensionOfBenefitsNonforfeitureRiderAmount);

  return {
    params:
      inflation === null
        ? quote.params
        : { ...quote.params, inflationProtectionPremium: inflation },
    extensionOfBenefitsNonforfeitureRiderAmount:
      nonforfeiture === null
        ? quote.extensionOfBenefitsNonforfeitureRiderAmount
        : String(nonforfeiture),
  };
}

/**
 * True when the quote is shown at exactly the premium that was requested — the product
 * honored it with no clamp and no pin. Only these quotes are directly comparable at that
 * premium; the rest are displayed at some other premium, so their ROI answers a different
 * question.
 */
export const isPremiumAsRequested = (quote: Pick<PricedQuote, 'premiumClamped'>): boolean =>
  !quote.premiumClamped;

/**
 * Adjusts a quote's parameters based on a new premium amount.
 * MoneyGuard Fixed Advantage 2025 and OneAmerica AssetCare use premium band
 * correction for non-linear pricing. OneAmerica uses separate correction
 * factors for death benefit vs total LTC benefit.
 * MoneyGuard also applies a floor guarantee ensuring death benefit never
 * falls below premium. All other products use simple linear scaling.
 * CareMatters Annuity also re-prices its two rider premiums, which are a
 * share of the premium.
 */
export const adjustQuoteForPremium = <T extends PricedQuote>(
  originalQuote: T,
  newPremiumAmount: number,
): T => {
  if (!originalQuote.premiumAmount || originalQuote.premiumAmount <= 0) {
    return originalQuote;
  }

  // A quote can only be issued at a premium within its own [investmentMin, investmentMax]
  // range. Clamp to that range so the displayed cost/coverage/ROI (and the downstream
  // illustration PDF and saved policy) reflect a premium the product can actually honor.
  //
  // A non-positive bound means "unknown" (see toKnownInvestmentBound), not "unbounded".
  // Treating it as no-clamp let linear carriers scale benefits to any premium while holding
  // ROI constant, so an implausible premium looked as good as a realistic one. With bounds
  // unknown the only premium known to be issuable is the quote's own, so pin there.
  // Only the bound in the direction of travel is binding, so a row missing just one bound
  // keeps its adjustability in the other direction.
  const minBound = toKnownInvestmentBound(originalQuote.investmentMin);
  const maxBound = toKnownInvestmentBound(originalQuote.investmentMax);

  let clampedPremium = newPremiumAmount;
  let premiumClamped: PremiumAdjustmentLimit | undefined;

  if (newPremiumAmount > originalQuote.premiumAmount) {
    if (maxBound === undefined) {
      clampedPremium = originalQuote.premiumAmount;
      premiumClamped = 'max-unknown';
    } else if (newPremiumAmount > maxBound) {
      clampedPremium = maxBound;
      premiumClamped = 'max';
    }
  } else if (newPremiumAmount < originalQuote.premiumAmount) {
    if (minBound === undefined) {
      clampedPremium = originalQuote.premiumAmount;
      premiumClamped = 'min-unknown';
    } else if (newPremiumAmount < minBound) {
      clampedPremium = minBound;
      premiumClamped = 'min';
    }
  }

  let factors = getScalingFactorsForQuote(originalQuote, clampedPremium);

  // When a OneAmerica target exceeds the matching carrier calibration curve, or a Lincoln
  // target buys a face amount above its issue limit, use the highest premium the carrier can
  // price. This keeps the card and generated illustration aligned.
  if (
    factors.outOfRange &&
    factors.maximumPremium !== undefined &&
    factors.maximumPremium >= originalQuote.premiumAmount &&
    clampedPremium > factors.maximumPremium
  ) {
    clampedPremium = factors.maximumPremium;
    premiumClamped = 'max';
    factors = getScalingFactorsForQuote(originalQuote, clampedPremium);
  }

  const { db: dbFactor, ltc: ltcFactor, outOfRange } = factors;

  // Remaining out-of-range cases have no safe scaling factor, so show the original quote.
  if (outOfRange) {
    clampedPremium = originalQuote.premiumAmount;
    premiumClamped = 'not-priceable';
  }

  // Pinning can land exactly on the requested premium, in which case nothing was limited.
  if (clampedPremium === newPremiumAmount) {
    premiumClamped = undefined;
  }

  let scaledDeathBenefit = originalQuote.deathBenefitInitial * dbFactor;

  if (originalQuote.fundingPolicyType === FundingPolicyType.moneyGuardFixedAdvantage) {
    scaledDeathBenefit = Math.max(scaledDeathBenefit, clampedPremium);
  }

  const adjustedQuote: T = {
    ...originalQuote,
    premiumAmount: clampedPremium,
    premiumClamped,

    deathBenefitInitial: scaledDeathBenefit,
    deathBenefitMinimumGuaranteedIndividual: calculateMinGuaranteedDeathBenefit(
      originalQuote.fundingPolicyType,
      scaledDeathBenefit,
      originalQuote.deathBenefitMinimumGuaranteedIndividual,
      dbFactor,
      originalQuote.usaState,
    ),
    deathBenefitMinimumGuaranteedJoint: scaleValue(
      originalQuote.deathBenefitMinimumGuaranteedJoint,
      dbFactor,
    ),

    ltcBenefitMonthlyIndividual: originalQuote.ltcBenefitMonthlyIndividual * dbFactor,
    ltcBenefitAnnualIndividual: scaleValue(originalQuote.ltcBenefitAnnualIndividual, dbFactor),
    ltcBenefitTotal: originalQuote.ltcBenefitTotal * ltcFactor,
    maxMonthlyLTCBenefitReimbursement: scaleValue(
      originalQuote.maxMonthlyLTCBenefitReimbursement,
      dbFactor,
    ),
    maxMonthlyLTCBenefitIndemnity: scaleValue(
      originalQuote.maxMonthlyLTCBenefitIndemnity,
      dbFactor,
    ),

    ltcBenefitDurationMonths: originalQuote.ltcBenefitDurationMonths,
    investmentMin: originalQuote.investmentMin,
    investmentMax: originalQuote.investmentMax,

    ...(isCareMattersAnnuity(originalQuote.fundingPolicyType)
      ? scaleCareMattersAnnuityRiderPremiums(originalQuote, clampedPremium)
      : {}),

    projectedCostCoverage: undefined,
    roi: undefined,
    roiValues: undefined,
    formattedROI: undefined,
  };

  return adjustedQuote;
};

/**
 * Reuses the fixed-premium modeler at the customer's minimum. Carrier bounds and
 * pricing tables may raise or pin the quote; keep it only when that validated result
 * still falls inside the inclusive budget.
 */
export function adjustQuoteForPremiumRange<T extends PricedQuote>(
  originalQuote: T,
  range: { min: number; max: number },
): T | null {
  const minimum = Math.min(range.min, range.max);
  const maximum = Math.max(range.min, range.max);
  const validatedMinimum = toKnownInvestmentBound(originalQuote.investmentMin);
  const target = Math.max(minimum, validatedMinimum ?? minimum);
  if (target > maximum) return null;

  const adjusted = adjustQuoteForPremium(originalQuote, target);

  return adjusted.premiumAmount >= minimum && adjusted.premiumAmount <= maximum ? adjusted : null;
}

export const adjustQuotesForPremium = <T extends PricedQuote>(
  originalQuotes: T[],
  newPremiumAmount: number,
): T[] => originalQuotes.map(quote => adjustQuoteForPremium(quote, newPremiumAmount));

/**
 * Adjusts an EquiTrust quote's LTC benefits based on a new coverage ratio
 * LTC benefits scale proportionally with coverage ratio changes
 * Premium and death benefit remain unchanged
 */
export const adjustQuoteForCoverageRatio = <T extends PricedQuote>(
  originalQuote: T,
  newCoverageRatio: number,
): T => {
  const originalCoverageRatio = originalQuote.coverageRatio;
  if (!originalCoverageRatio || originalCoverageRatio <= 0) {
    return originalQuote;
  }

  const minRatio = originalQuote.minCoverageRatio || originalCoverageRatio;
  const maxRatio = originalQuote.maxCoverageRatio || originalCoverageRatio;
  const clampedRatio = Math.max(minRatio, Math.min(maxRatio, newCoverageRatio));

  const scalingFactor = clampedRatio / originalCoverageRatio;

  return {
    ...originalQuote,
    coverageRatio: clampedRatio,
    ltcBenefitMonthlyIndividual: originalQuote.ltcBenefitMonthlyIndividual * scalingFactor,
    ltcBenefitAnnualIndividual: scaleValue(originalQuote.ltcBenefitAnnualIndividual, scalingFactor),
    ltcBenefitTotal: originalQuote.ltcBenefitTotal * scalingFactor,
    projectedCostCoverage: undefined,
    roi: undefined,
    roiValues: undefined,
    formattedROI: undefined,
  };
};
